import type { ReactElement } from 'react';
import { Badge } from '@vwpfs/mary-air-lib/components/air/05-atoms/Badge';
import { ThemePalette, ThemePaletteState } from '@vwpfs/mary-air-lib/theme/_Types';
import { useVehicleOverrideHistory } from '../hooks/useVehicleOverrideHistory';
import { formatOverrideDate } from '../lib/override-note';
import { PRODUCT_LABELS, type MobilistOverrideEvent } from '../types/vehicle';
import { VapModal } from './VapModal';

interface Props {
  readonly vehicleId: string;
  readonly vehicleLabel: string;
  readonly onClose: () => void;
}

const ACTION_LABELS: Record<MobilistOverrideEvent['action'], string> = {
  RESERVE: 'Reserved',
  UNRESERVE: 'Unreserved',
  AD_BLOCK: 'Advertising changed'
};

/**
 * Reads one (broker, product) pair the way the advertising modal labels its cells, so the same
 * change is recognisable in both places.
 */
function pairLabel({ broker, product }: { broker: string; product: string }): string {
  return `${broker} · ${PRODUCT_LABELS[product as keyof typeof PRODUCT_LABELS] ?? product}`;
}

function changeSummary(event: MobilistOverrideEvent): ReactElement | null {
  if (event.action !== 'AD_BLOCK') {
    return null;
  }

  return (
    <div className="vap-override-history__changes">
      {event.blocksAdded.map((pair) => (
        <small key={`added-${pair.broker}-${pair.product}`} className="vap-override-history__blocked">
          Blocked: {pairLabel(pair)}
        </small>
      ))}
      {event.blocksRemoved.map((pair) => (
        <small key={`removed-${pair.broker}-${pair.product}`} className="vap-override-history__unblocked">
          Unblocked: {pairLabel(pair)}
        </small>
      ))}
    </div>
  );
}

function entry(event: MobilistOverrideEvent, index: number): ReactElement {
  const reason = event.reason?.trim();

  return (
    <li key={`${event.at}-${index}`} className="vap-override-history__entry">
      <div className="vap-override-history__meta">
        <Badge
          theme={
            event.action === 'UNRESERVE'
              ? { palette: ThemePalette.CONTRAST_TERTIARY }
              : { paletteState: ThemePaletteState.WARNING }
          }
        >
          {ACTION_LABELS[event.action]}
        </Badge>
        <small className="vap-override-history__actor">
          {event.actor} · {formatOverrideDate(event.at)}
        </small>
      </div>

      {changeSummary(event)}

      <p className={`vap-override-history__reason${reason ? '' : ' vap-override-history__reason--empty'}`}>
        {reason || 'No reason given'}
      </p>
    </li>
  );
}

/**
 * The full override trail for one vehicle.
 *
 * Opened per row rather than loaded with the list: the badges already carry the current reason, so
 * the timeline is only worth fetching for a vehicle someone is actually asking about. Rendered only
 * while open — unlike its sibling modals it holds a query, and mounting that for every row would
 * fetch a timeline nobody asked for.
 */
export function OverrideHistoryModal({ vehicleId, vehicleLabel, onClose }: Props): ReactElement {
  const { data, isPending, isError, error } = useVehicleOverrideHistory(vehicleId);

  return (
    <VapModal
      id="vap-override-history-modal"
      title={`Override history — ${vehicleLabel}`}
      onClose={onClose}
      showCloseIcon
    >
      <div className="vap-override-history">
        {isPending && <p className="vap-override-history__status">Loading history…</p>}

        {isError && (
          <p className="vap-override-history__error">
            Could not load the history. {error?.message ?? 'Please try again.'}
          </p>
        )}

        {data?.length === 0 && (
          // Distinct from a load failure: nobody has ever overridden this vehicle.
          <p className="vap-override-history__status">
            No reserve or advertising changes have been made to this vehicle.
          </p>
        )}

        {!!data?.length && <ul className="vap-override-history__list">{data.map(entry)}</ul>}
      </div>
    </VapModal>
  );
}
